import AppIconButton from "../../components/form/AppIconButton";
import React, { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Backdrop from "@mui/material/Backdrop";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";

import SidenavCollapse from "../Sidenav/SidenavCollapse";
import SidenavCollapsible from "../Sidenav/SidenavCollapsible";
import SidenavSubItem from "../Sidenav/SidenavSubItem";

// Custom styles for the Sidenav
import SidenavRoot from "../Sidenav/SidenavRoot";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

import {
  useMaterialUIController,
  setMiniSidenav,
  setSidenavPinned,
  setTransparentSidenav,
  setWhiteSidenav,
} from "../../context";
import { canAccessRoute } from "../../utils/authorization";
import { useDeploymentMode } from "../../hooks/useDeploymentMode";
import { isNavRouteActive } from "../../navigation/navActive";
const NAV_I18N_KEYS: Record<string, string> = {
  home: "nav.home",
  logs: "nav.logs",
  "ai-context": "nav.aiContext",
  report: "nav.report",
  dashboards: "nav.dashboards",
  widgets: "nav.widgetsQueries",
  "dashboard-queries": "nav.dashboardQueries",
  anomalies: "nav.anomalies",
  "insight-report-jobs": "nav.insightReportJobs",
  "ai-guide": "nav.aiGuide",
  "data-structure-analytics": "nav.dataStructureAnalytics",
  "chat-query-examples": "nav.chatQueryExamples",
  "mail-settings": "nav.mailSettings",
  "super-admin": "nav.superAdmin",
  "sa-dashboard": "superAdmin.tabs.dashboard",
  "sa-plans": "superAdmin.tabs.plans",
  "sa-customers": "superAdmin.tabs.customers",
  "sa-datasources": "superAdmin.tabs.datasources",
  "sa-mail": "superAdmin.tabs.mail",
  "sa-workspaces": "superAdmin.tabs.workspaces",
  "sa-users": "superAdmin.tabs.users",
  "sa-invites": "superAdmin.tabs.invites",
  "sa-embeds": "superAdmin.tabs.embeds",
  "sa-integrations": "superAdmin.tabs.integrations",
  "sa-audit": "superAdmin.tabs.audit",
  "sa-errors": "superAdmin.tabs.errors",
  "workspace-settings": "nav.workspace",
  "openapi-catalogs": "nav.openapiCatalogs",
  "adaptor-discovery": "nav.adaptorDiscovery",
  "semantic-models": "nav.semanticModels",
  "warehouse-usage": "nav.warehouseUsage",
  "schema-relations": "nav.schemaRelations",
  "business-errors": "nav.businessErrors",
  "audit-log": "nav.auditLog",
  "data-prep": "menu.data-prep",
  "admin-prep": "menu.admin-prep",
  "data-sources": "nav.dataSources",
  ingest: "nav.ingest",
  "admin-ingest": "nav.ingestAdmin",
  "admin-data-adaptors": "nav.dataAdaptorsAdmin",
  "vpn-profiles": "nav.vpnProfiles",
  docs: "nav.docs",
  "docs-api": "nav.api",
  "release-notes": "nav.releaseNotes",
};

/**
 * Menu groups ordered by product dependency:
 * consume → query/monitor → pipeline → AI context → configure → observe → help.
 */
const MENU_GROUPS: Record<string, { nameKey: string; icon: string }> = {
  analiz: {
    nameKey: "nav.analiz",
    icon: "analytics",
  },
  sorgular: {
    nameKey: "nav.sorgular",
    icon: "query_stats",
  },
  veriHatti: {
    nameKey: "nav.veriHatti",
    icon: "account_tree",
  },
  zeka: {
    nameKey: "nav.zeka",
    icon: "auto_awesome",
  },
  ayarlar: {
    nameKey: "nav.ayarlar",
    icon: "settings",
  },
  gunluk: {
    nameKey: "nav.gunluk",
    icon: "receipt_long",
  },
};

/** Stable sidenav group order (independent of route declaration order). */
const MENU_GROUP_ORDER = [
  "analiz",
  "sorgular",
  "veriHatti",
  "zeka",
  "ayarlar",
  "gunluk",
] as const;

/** Child order within each group (dependency / workflow order). */
const MENU_CHILD_ORDER: Record<string, readonly string[]> = {
  analiz: ["dashboards", "widgets"],
  sorgular: ["anomalies", "insight-report-jobs"],
  veriHatti: ["data-sources", "data-prep", "schema-relations", "adaptor-discovery", "semantic-models", "warehouse-usage", "openapi-catalogs"],
  ayarlar: ["workspace-settings", "vpn-profiles", "logs"],
};

function sortMenuChildren<T extends { key?: string }>(parentKey: string, routes: T[]): T[] {
  const order = MENU_CHILD_ORDER[parentKey];
  if (!order?.length) return routes;
  const rank = new Map(order.map((key, index) => [key, index]));
  return [...routes].sort((a, b) => {
    const ai = rank.has(a.key || "") ? (rank.get(a.key || "") as number) : 1000;
    const bi = rank.has(b.key || "") ? (rank.get(b.key || "") as number) : 1000;
    return ai - bi;
  });
}



function Sidenav({ color = "info", brand = "", brandMark = "", brandName, routes, userRoles = [], ...rest }: any) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const { vpnEnabled, onPrem } = useDeploymentMode();
  const sidenav = custom.widgets.sidenav;
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    sidenavPinned,
  } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");
  const [isMobile, setIsMobile] = useState(false);

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      const isMobileDevice = window.innerWidth < 992;
      setIsMobile(isMobileDevice);
      
      if (isMobileDevice) {
        // Hide sidenav completely on mobile devices
        setMiniSidenav(dispatch, true);
        setTransparentSidenav(dispatch, false);
        setWhiteSidenav(dispatch, false);
      } else if (window.innerWidth < 1200) {
        // Switch to mini mode on tablet devices
        setMiniSidenav(dispatch, true);
        setTransparentSidenav(dispatch, false);
        setWhiteSidenav(dispatch, false);
      } else {
        // Desktop: default collapsed; preserved when sidenav is pinned
        if (!sidenavPinned) {
          setMiniSidenav(dispatch, true);
        }
      }
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location, transparentSidenav, whiteSidenav, sidenavPinned]);

  const resolveRouteName = (key: string, fallback: string) =>
    NAV_I18N_KEYS[key] ? t(NAV_I18N_KEYS[key]) : fallback;

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = () => {
    const menuRoutes = routes.filter(
      (route: any) =>
        route.menu &&
        (!route.requiresVpn || vpnEnabled) &&
        (!route.requiresSaaS || !onPrem) &&
        canAccessRoute(userRoles, route.allowedRoles, route.key)
    );
    const result: any[] = [];
    
    // Group routes by parent
    const groupedRoutes: any = {};
    const standaloneRoutes: any[] = [];
    
    menuRoutes.forEach((route: any) => {
      if (route.parent) {
        if (!groupedRoutes[route.parent]) {
          groupedRoutes[route.parent] = [];
        }
        groupedRoutes[route.parent].push(route);
      } else {
        standaloneRoutes.push(route);
      }
    });
    
    const renderStandaloneItem = ({
      type,
      name,
      icon,
      title,
      noCollapse,
      key,
      href,
      route,
      activePaths,
    }: any) => {
      const displayName = resolveRouteName(key, name);
      if (type === "collapse") {
        return href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={sidenav.navLink}
          >
            <SidenavCollapse
              name={displayName}
              icon={icon}
              active={isNavRouteActive(location.pathname, collapseName, route, key, activePaths)}
              noCollapse={noCollapse}
            />
          </Link>
        ) : (
          <NavLink key={key} to={route}>
            <SidenavCollapse
              name={displayName}
              icon={icon}
              active={isNavRouteActive(location.pathname, collapseName, route, key, activePaths)}
            />
          </NavLink>
        );
      }
      if (type === "title") {
        return (
          <Typography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </Typography>
        );
      }
      if (type === "divider") {
        return (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }
      return null;
    };

    const byMenuOrder = (a: any, b: any) => (a.menuOrder ?? 1000) - (b.menuOrder ?? 1000);
    const topStandalone = standaloneRoutes.filter((r: any) => !r.menuBottom).sort(byMenuOrder);
    const bottomStandalone = standaloneRoutes.filter((r: any) => r.menuBottom).sort(byMenuOrder);

    // Top standalone → groups → bottom standalone (e.g. Documentation last).
    topStandalone.forEach((routeItem: any) => {
      result.push(renderStandaloneItem(routeItem));
    });

    const orderedParentKeys = [
      ...MENU_GROUP_ORDER.filter((key) => groupedRoutes[key]),
      ...Object.keys(groupedRoutes).filter(
        (key) => !(MENU_GROUP_ORDER as readonly string[]).includes(key)
      ),
    ];

    orderedParentKeys.forEach((parentKey) => {
      const parentRoutes = sortMenuChildren(parentKey, groupedRoutes[parentKey]);
      const groupConfig = MENU_GROUPS[parentKey];
      if (!groupConfig) {
        return;
      }

      const subItems = parentRoutes.map(({ type, name, icon, title, noCollapse, key, href, route, activePaths }: any) => {
        const displayName = resolveRouteName(key, name);
        if (type === "collapse") {
          return href ? (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={sidenav.navLink}
            >
              <SidenavSubItem
                name={displayName}
                icon={icon}
                active={isNavRouteActive(location.pathname, collapseName, route, key, activePaths)}
                noCollapse={noCollapse}
              />
            </Link>
          ) : (
            <NavLink key={key} to={route}>
              <SidenavSubItem
                name={displayName}
                icon={icon}
                active={isNavRouteActive(location.pathname, collapseName, route, key, activePaths)}
              />
            </NavLink>
          );
        }
        if (type === "title") {
          return (
            <Typography
              key={key}
              color={textColor}
              display="block"
              variant="caption"
              fontWeight="bold"
              textTransform="uppercase"
              pl={3}
              mt={2}
              mb={1}
              ml={1}
            >
              {title}
            </Typography>
          );
        }
        return null;
      }).filter(Boolean);

      const isGroupActive = parentRoutes.some(({ key, route, activePaths }: any) =>
        isNavRouteActive(location.pathname, collapseName, route, key, activePaths)
      );

      result.push(
        <SidenavCollapsible
          key={parentKey}
          name={t(groupConfig.nameKey)}
          icon={groupConfig.icon}
          active={isGroupActive}
        >
          {subItems}
        </SidenavCollapsible>
      );
    });

    bottomStandalone.forEach((routeItem: any) => {
      result.push(renderStandaloneItem(routeItem));
    });

    return result as any[];
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {!miniSidenav && isMobile && (
        <Backdrop
          open
          onClick={closeSidenav}
          sx={sidenav.mobileBackdrop}
        />
      )}
      
      {/* Sidenav - Don't render if miniSidenav is true on mobile devices */}
      {(!isMobile || !miniSidenav) && (
        <SidenavRoot
          {...rest}
          variant="permanent"
          ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
        >
          <Box sx={sidenav.container}>
            <Box sx={sidenav.header(miniSidenav)}>
              <Box
                sx={sidenav.mobileCloseWrap}
                onClick={closeSidenav}
              >
                <AppIconButton size="small" aria-label={t("navbar.closeMenu")}>
                  <Icon sx={sidenav.closeIcon}>close</Icon>
                </AppIconButton>
              </Box>
              <Box component={NavLink} to="/report" sx={sidenav.brandRow(miniSidenav)}>
                {(brandMark || brand) && (
                  <Box
                    component="img"
                    src={brandMark || brand}
                    alt={brandName || "Connect AI"}
                    sx={sidenav.brandImage(miniSidenav)}
                  />
                )}
                <Box component="span" sx={sidenav.logoLabel(miniSidenav)} aria-hidden={miniSidenav}>
                  <Box component="span" sx={sidenav.logoConnect}>
                    Connect
                  </Box>
                  <Box component="span" sx={sidenav.logoAi}>
                    AI
                  </Box>
                </Box>
              </Box>
            </Box>
            <List sx={sidenav.list}>{renderRoutes()}</List>
            {!isMobile && (
              <Box
                sx={sidenav.footer({ transparentSidenav, whiteSidenav })}
              >
                <Tooltip title={miniSidenav ? t("nav.expandMenu") : t("nav.collapseMenu")} arrow>
                  <AppIconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setMiniSidenav(dispatch, !miniSidenav);
                    }}
                    sx={sidenav.footerToggleButton(textColor)}
                  >
                    <Icon fontSize="small">
                      {miniSidenav ? "keyboard_double_arrow_right" : "keyboard_double_arrow_left"}
                    </Icon>
                  </AppIconButton>
                </Tooltip>
                <Tooltip
                  title={
                    sidenavPinned
                      ? t("nav.unpinSidebar")
                      : t("nav.pinSidebar")
                  }
                  arrow
                >
                  <AppIconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setSidenavPinned(dispatch, !sidenavPinned);
                    }}
                    sx={sidenav.footerPinButton({ textColor, sidenavPinned })}
                  >
                    {sidenavPinned ? (
                      <PushPinIcon sx={sidenav.pinIcon} />
                    ) : (
                      <PushPinOutlinedIcon sx={sidenav.pinIcon} />
                    )}
                  </AppIconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </SidenavRoot>
      )}
    </>
  );
}

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  brand: PropTypes.string,
  brandMark: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string),
};

export default Sidenav;
